import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AgentRun, AgentTask, Engagement } from '@ai-secure-twin/shared';
import { ScoreBadge, useAgentRuns } from './AiSuggestion';
import { api, ApiError } from '../../lib/api';

const TASK_LABEL: Record<AgentTask, string> = {
  SUMMARISE: 'Summary',
  PROPOSE_NEXT_STAGE: 'Next-step proposal',
  SUGGEST_TRIAGE: 'Triage suggestions',
};

/** A run is still moving while it is queued or running, so the panel keeps polling. */
const isPending = (run: AgentRun) => run.status === 'QUEUED' || run.status === 'RUNNING';

function RunResult({ run }: { run: AgentRun }) {
  if (run.status === 'FAILED') {
    return (
      <p className="login-error" role="alert">
        {run.error ?? 'The run failed.'}
      </p>
    );
  }
  if (!run.result) return null;

  const r = run.result;
  return (
    <div className="run-result">
      <p>{r.summary}</p>
      <div className="run-proposal">
        <ScoreBadge score={r.confidence} />
        {r.proposedNextStage ? (
          <p className="meta">
            Proposes <strong>{r.proposedNextStage}</strong> — {r.rationale}
          </p>
        ) : (
          // A null proposal is a real answer: the agent declining to advise is not a failure.
          <p className="meta">Proposes no stage change yet. {r.rationale}</p>
        )}
      </div>
      {run.task === 'SUGGEST_TRIAGE' ? (
        <p className="meta">
          <strong>
            {r.findingSuggestions.length} finding
            {r.findingSuggestions.length === 1 ? '' : 's'}
          </strong>{' '}
          with a suggestion — open each finding below to see it, with its evidence and score.
        </p>
      ) : null}
      {r.risks.length > 0 ? (
        <>
          <span className="scope-label">Risks</span>
          <ul className="scope-list">
            {r.risks.map((risk) => (
              <li key={risk}>{risk}</li>
            ))}
          </ul>
        </>
      ) : null}
      {r.questionsForHuman.length > 0 ? (
        <>
          <span className="scope-label">Questions for you</span>
          <ul className="scope-list">
            {r.questionsForHuman.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="meta">
        Proposal only — the agent never moves a stage or approves anything. Prompt {r.promptVersion}
        .
      </p>
    </div>
  );
}

export function AgentRunPanel({ engagement }: { engagement: Engagement }) {
  const engagementId = engagement.id;
  const queryClient = useQueryClient();
  // Polls only while a run is in flight; shared with the panels that show its suggestions.
  const runs = useAgentRuns(engagementId);

  const request = useMutation({
    mutationFn: (task: AgentTask) => api.requestAgentRun(engagementId, { task }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['agentRuns', engagementId] });
    },
  });

  const items = [...(runs.data?.items ?? [])].sort((a, b) =>
    b.requestedAt.localeCompare(a.requestedAt),
  );
  const latest = items[0];
  const busy = request.isPending || (latest ? isPending(latest) : false);
  const asks: { task: AgentTask; label: string }[] = [
    ...(engagement.stage === 'TRIAGE'
      ? [{ task: 'SUGGEST_TRIAGE' as const, label: 'Suggest triage' }]
      : []),
    { task: 'PROPOSE_NEXT_STAGE', label: 'Propose next step' },
    { task: 'SUMMARISE', label: 'Summarise' },
  ];

  return (
    <section className="card panel" aria-labelledby="agent-title">
      <div className="panel-head">
        <h2 id="agent-title" className="panel-title">
          Engagement Lead
        </h2>
        <div className="run-asks">
          {asks.map((a) => (
            <button
              key={a.task}
              type="button"
              className="btn btn-outline btn-sm"
              disabled={busy}
              onClick={() => request.mutate(a.task)}
            >
              {request.isPending && request.variables === a.task ? 'Asking…' : a.label}
            </button>
          ))}
        </div>
      </div>

      {request.error ? (
        <p className="login-error" role="alert">
          {request.error instanceof ApiError ? request.error.message : 'Could not start the run.'}
        </p>
      ) : null}

      {runs.isLoading ? (
        <p className="muted">Loading runs…</p>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>No runs yet.</p>
          <p className="muted">
            The Engagement Lead reads this engagement and suggests what to do, scoring its own
            confidence out of 10. It suggests only — every decision stays with a person.
          </p>
        </div>
      ) : (
        <div className="run-list">
          {items.map((run) => (
            <article key={run.id} className="run">
              <div className="run-head">
                <span>{TASK_LABEL[run.task]}</span>
                <span className={`status ${isPending(run) ? 'status-waiting' : 'status-done'}`}>
                  {run.status}
                </span>
              </div>
              {isPending(run) ? (
                <p className="muted" style={{ fontSize: 13 }}>
                  Working… this panel updates itself.
                </p>
              ) : (
                <RunResult run={run} />
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
